import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { 
  Square, RectangleHorizontal, RectangleVertical, 
  PanelLeftOpen, PanelRightOpen, Unlock, 
  Minus, Plus, ScanEye, type LucideProps 
} from '@/components/ui/lucide-memo';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { AspectRatio } from '#types/types';
import { useKernel } from '@/kernel/Kernel';
import {
  useKernelStore,
  useKernelStoreApi,
} from '@/kernel/kernelStores';
import { formatViewportZoomLabel } from '@/kernel/useViewports';

// --- Memoized Icons ---
const MemoizedSquareIcon: React.FC<LucideProps> = React.memo((props) => <Square {...props} />);
const MemoizedRectangleHorizontalIcon: React.FC<LucideProps> = React.memo((props) => <RectangleHorizontal {...props} />);
const MemoizedRectangleVerticalIcon: React.FC<LucideProps> = React.memo((props) => <RectangleVertical {...props} />);
const MemoizedPanelLeftOpenIcon: React.FC<LucideProps> = React.memo((props) => <PanelLeftOpen {...props} />);
const MemoizedPanelRightOpenIcon: React.FC<LucideProps> = React.memo((props) => <PanelRightOpen {...props} />);
const MemoizedUnlockIcon: React.FC<LucideProps> = React.memo((props) => <Unlock {...props} />);
const MemoizedMinusIcon: React.FC<LucideProps> = React.memo((props) => <Minus {...props} />);
const MemoizedPlusIcon: React.FC<LucideProps> = React.memo((props) => <Plus {...props} />);
const MemoizedScanEyeIcon: React.FC<LucideProps> = React.memo((props) => <ScanEye {...props} />);

// --- Constants ---
const BAR_HEIGHT_PX = 40;

interface RatioPreset {
  label: string;
  value: AspectRatio;
  Icon: React.FC<LucideProps>;
}

const PRESETS: RatioPreset[] = [
  { label: 'Free', value: null, Icon: MemoizedUnlockIcon },
  { label: '1:1', value: 1, Icon: MemoizedSquareIcon },
  { label: '16:9', value: 16 / 9, Icon: MemoizedRectangleHorizontalIcon },
  { label: '9:16', value: 9 / 16, Icon: MemoizedRectangleVerticalIcon }
];

const ratiosMatch = (left: AspectRatio, right: AspectRatio): boolean => {
  if (left === null || right === null) return false;
  return Math.abs(left - right) <= 0.0001;
};

interface BarButtonProps {
  onClick: () => void;
  icon?: React.FC<LucideProps>;
  children?: React.ReactNode;
  tooltipText: string;
  isActive?: boolean;
  className?: string;
  buttonId?: string;
  pulseToken?: number;
}

/** Exported so other canvases can build the same control island rather than
 *  re-inventing the look — the Slides zoom bar uses it. Purely presentational:
 *  no Studio state, no kernel coupling. */
export const BarButton: React.FC<BarButtonProps> = React.memo(({ onClick, icon: Icon, children, tooltipText, isActive, className, buttonId, pulseToken = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const pulseAnimationRef = useRef<Animation | null>(null);
  const handleClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    onClick();
  }, [onClick]);
  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  useEffect(() => {
    if (pulseToken <= 0 || !buttonRef.current) return;
    pulseAnimationRef.current?.cancel();
    pulseAnimationRef.current = buttonRef.current.animate(
      [
        { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.7)', offset: 0 },
        { transform: 'scale(1.05)', boxShadow: '0 0 0 10px rgba(255, 255, 255, 0)', offset: 0.7 },
        { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)', offset: 1 },
      ],
      { duration: 1600, easing: 'ease-out' }
    );
  }, [pulseToken]);

  const fgColor = isActive ? '#0D0D0D' : (isHovered ? '#FFFFFF' : '#B4B4B4');
  const finalBgColor = isActive ? '#ECECEC' : (isHovered ? '#2A2A2A' : 'transparent');

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          ref={buttonRef}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          id={buttonId}
          className={`flex items-center justify-center rounded-full transition-colors duration-150 ease-in-out ${className || ''}`}
          style={{
            backgroundColor: finalBgColor,
            color: fgColor,
            padding: '6px',
            minWidth: '28px',
            height: '28px',
          }}
        >
          {Icon && <Icon size={16} />}
          {children}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        <p>{tooltipText}</p>
      </TooltipContent>
    </Tooltip>
  );
});
BarButton.displayName = 'BarButton';

interface RatioPresetButtonProps {
  preset: RatioPreset;
  isActive: boolean;
  pulseToken: number;
  onSelect: (ratio: AspectRatio) => void;
}

/** Carries the preset value to the handler itself: an inline arrow in the
 *  PRESETS map would hand a new onClick to every BarButton on each ratio
 *  change, re-rendering the whole row for a two-button state change. */
const RatioPresetButton: React.FC<RatioPresetButtonProps> = React.memo(({ preset, isActive, pulseToken, onSelect }) => {
  const handleClick = useCallback(() => onSelect(preset.value), [onSelect, preset.value]);
  return (
    <BarButton
      onClick={handleClick}
      tooltipText={preset.label}
      isActive={isActive}
      icon={preset.Icon}
      pulseToken={pulseToken}
    />
  );
});
RatioPresetButton.displayName = 'RatioPresetButton';

const Separator = () => (
  <div className="h-4/5 w-px bg-[#333333] mx-1 self-center" style={{ maxHeight: '20px' }} />
);

const CustomRatioDisplay: React.FC<{ ratio: number; pulseToken?: number }> = ({ ratio, pulseToken = 0 }) => {
  const displayRef = useRef<HTMLDivElement | null>(null);
  const pulseAnimationRef = useRef<Animation | null>(null);

  useEffect(() => {
    if (pulseToken <= 0 || !displayRef.current) return;
    pulseAnimationRef.current?.cancel();
    pulseAnimationRef.current = displayRef.current.animate(
      [
        { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 255, 255, 0.7)', offset: 0 },
        { transform: 'scale(1.05)', boxShadow: '0 0 0 10px rgba(255, 255, 255, 0)', offset: 0.7 },
        { transform: 'scale(1)', boxShadow: '0 0 0 0 rgba(255, 255, 255, 0)', offset: 1 },
      ],
      { duration: 1600, easing: 'ease-out' }
    );
  }, [pulseToken]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          ref={displayRef}
          className="p-1.5 flex items-center justify-center"
          style={{
            borderRadius: '9999px',
            backgroundColor: '#ECECEC',
            minWidth: '50px',
            height: '28px',
            cursor: 'default',
          }}
        >
          <span className="text-xs font-semibold" style={{ color: '#0D0D0D' }}>
            {ratio.toFixed(2)}
          </span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="top" sideOffset={8}>
        <p>Custom Ratio</p>
      </TooltipContent>
    </Tooltip>
  );
};

interface BottomBarProps {
  projectId: string;
}

const BottomBar: React.FC<BottomBarProps> = React.memo(({ projectId }) => {
  const { kernel } = useKernel();
  const kernelStore = useKernelStoreApi();
  const zoomLevel = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const value = runtime?.zoomLevel;
    return typeof value === 'number' && Number.isFinite(value) ? value : 1;
  });
  const workspaceId = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    return typeof runtime?.UIWorkspaceId === 'string' ? runtime.UIWorkspaceId.trim() : '';
  });
  const aspectRatio = useKernelStore((state) => {
    const runtime = state.inventory.Project.registry[projectId]?.runtime;
    const activeWorkspaceId =
      typeof runtime?.UIWorkspaceId === 'string' ? runtime.UIWorkspaceId.trim() : '';
    const activeWorkspace = state.inventory.StudioWorkspace.registry[activeWorkspaceId];
    return activeWorkspace?.aspectRatio ?? null;
  });
  const aspectRatioEnforcementPulse = useKernelStore((state) => {
    const pulse = state.inventory.Project.registry[projectId]?.runtime?.aspectRatioEnforcementPulse;
    return pulse?.workspaceId === workspaceId ? pulse : null;
  });
  const showResolutionHeatmap = useKernelStore(state => state.showResolutionHeatmap);

  // --- Handlers ---
  const handleSetRatio = useCallback((ratio: AspectRatio) => {
    if (!kernel.current || !projectId) return;
    kernel.current.setWorkspaceAspectRatio(projectId, kernel.current.getProjectUIWorkspaceId(projectId), ratio);
  }, [kernel, projectId]);

  const handleZoomFactorClick = useCallback(() => {
    if (!kernel.current) return;
    const didZoomOnCanvas = kernel.current.zoomOnCanvas(kernel.current.getProjectUIWorkspaceId(projectId));
    if (!didZoomOnCanvas) {
      kernel.current.zoomOnAllFrames(projectId);
    }
  }, [kernel, projectId]);

  const handleZoomIn = useCallback(() => kernel.current?.handleZoomInFromButton(projectId), [kernel, projectId]);
  const handleZoomOut = useCallback(() => kernel.current?.handleZoomOutFromButton(projectId), [kernel, projectId]);
  
  const handleToggleHeatmap = useCallback(() => {
    if (!kernel.current) return;
    const newState = !kernelStore.getState().showResolutionHeatmap;
    kernel.current.setShowResolutionHeatmap(newState);
    if(newState) kernel.current?.playUISound('HEATMAP', { volume: 0.4 });
  }, [kernel, kernelStore]);

  const handleToggleLeftSidebar = useCallback(() => {
    kernel.current?.toggleLeftPanel(true);
  }, [kernel]);

  const handleToggleRightSidebar = useCallback(() => {
    kernel.current?.toggleRightPanel(true);
  }, [kernel]);

  const stopper = useCallback((e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation(), []);

  // --- Derived State ---
  const isCustomRatio = useMemo(() => 
    aspectRatio !== null && !PRESETS.some(p => p.value === aspectRatio),
    [aspectRatio]
  );

  const formattedZoom = useMemo(() => formatViewportZoomLabel(zoomLevel), [zoomLevel]);

  const leftToggleNode = useMemo(() => (
    <BarButton
      onClick={handleToggleLeftSidebar}
      tooltipText="Toggle Left Sidebar"
      icon={MemoizedPanelLeftOpenIcon}
    />
  ), [handleToggleLeftSidebar]);

  const rightToggleNode = useMemo(() => (
    <BarButton
      onClick={handleToggleRightSidebar}
      tooltipText="Toggle Right Sidebar"
      icon={MemoizedPanelRightOpenIcon}
      buttonId="editor-toggle-sidebar"
    />
  ), [handleToggleRightSidebar]);

  const ratioControlsNode = useMemo(() => {
    const pulseToken = aspectRatioEnforcementPulse?.token ?? 0;
    const pulseRatio = aspectRatioEnforcementPulse?.ratio ?? null;

    return (
      <>
        {PRESETS.map((preset) => (
          <RatioPresetButton
            key={preset.label}
            preset={preset}
            onSelect={handleSetRatio}
            isActive={aspectRatio === preset.value}
            pulseToken={ratiosMatch(preset.value, pulseRatio) ? pulseToken : 0}
          />
        ))}
        {isCustomRatio && aspectRatio && (
          <>
            <Separator />
            <CustomRatioDisplay
              ratio={aspectRatio}
              pulseToken={ratiosMatch(aspectRatio, pulseRatio) ? pulseToken : 0}
            />
          </>
        )}
      </>
    );
  }, [handleSetRatio, aspectRatio, isCustomRatio, aspectRatioEnforcementPulse]);

  const zoomControlsNode = useMemo(() => (
    <>
      <BarButton
        onClick={handleToggleHeatmap}
        tooltipText="Resolution Heatmap"
        isActive={showResolutionHeatmap}
        icon={MemoizedScanEyeIcon}
      />
      <BarButton onClick={handleZoomOut} tooltipText="Zoom Out" icon={MemoizedMinusIcon} />
      <BarButton
        onClick={handleZoomFactorClick}
        tooltipText={`Zoom (${formattedZoom})`}
        className="min-w-[50px]"
      >
        <span className="text-xs">{formattedZoom}</span>
      </BarButton>
      <BarButton onClick={handleZoomIn} tooltipText="Zoom In" icon={MemoizedPlusIcon} />
    </>
  ), [handleToggleHeatmap, showResolutionHeatmap, handleZoomOut, handleZoomFactorClick, formattedZoom, handleZoomIn]);

  return (
    <>
      {/* --- DESKTOP LAYOUT (Split Islands) --- */}
      <div className="hidden min-[680px]:block pointer-events-none absolute inset-0 z-10 overflow-hidden">
        {/* Left Island: Ratio */}
        <div
          className="absolute bottom-2 left-6 flex items-center space-x-1 rounded-full border border-[#262626] p-[3px] shadow-[0_8px_24px_rgba(0,0,0,0.45)] pointer-events-auto"
          style={{ backgroundColor: '#1B1B1B', height: `${BAR_HEIGHT_PX - 4}px` }}
          onMouseDown={stopper} onDoubleClick={stopper}
          id="editor-aspect-ratio-bar"
        >
          {leftToggleNode}
          <Separator />
          {ratioControlsNode}
        </div>

        {/* Right Island: Zoom */}
        <div
          className="absolute bottom-2 right-6 flex items-center space-x-1 rounded-full border border-[#262626] p-[3px] shadow-[0_8px_24px_rgba(0,0,0,0.45)] pointer-events-auto"
          style={{ backgroundColor: '#1B1B1B', height: `${BAR_HEIGHT_PX - 4}px` }}
          onMouseDown={stopper} onDoubleClick={stopper}
          id="editor-zoom-bar"
        >
          {zoomControlsNode}
          <Separator />
          {rightToggleNode}
        </div>
      </div>

      {/* --- MOBILE LAYOUT (Stacked Rows) --- */}
      <div 
        className="min-[680px]:hidden absolute bottom-1 left-0 w-full px-1 flex flex-col gap-1 pointer-events-none z-20"
        onMouseDown={stopper} onDoubleClick={stopper}
      >
        {/* Row 1: Toggles (Constrained width to stay closer to center cluster) */}
        <div className="flex justify-between w-full mx-auto">
          <div className="pointer-events-auto p-0.5 rounded-full border border-[#262626] shadow-lg bg-[#1B1B1B]">
            {leftToggleNode}
          </div>
          <div className="pointer-events-auto p-0.5 rounded-full border border-[#262626] shadow-lg bg-[#1B1B1B]">
            {rightToggleNode}
          </div>
        </div>

        {/* Row 2: Combined Controls Centered */}
        <div className="flex justify-center w-full pointer-events-auto">
          <div className="flex items-center gap-0.5 p-0.5 rounded-full border border-[#262626] shadow-lg bg-[#1B1B1B] overflow-x-auto no-scrollbar max-w-full">
            {ratioControlsNode}
            <Separator />
            {zoomControlsNode}
          </div>
        </div>
      </div>
    </>
  );
});

BottomBar.displayName = 'BottomBar';
export default BottomBar;


